"use client";

import { useState, useRef } from "react";
import { useBlogTheme } from "@/contexts/blog-theme-context";

interface DataPoint {
  name: string;
  subLabel?: string;
  score: number;
  cost: number;
  company: "agentica" | "openai" | "anthropic";
  bold: boolean;
}

const data: DataPoint[] = [
  {
    name: "Agentica Opus 4.6 (120k) High",
    score: 85.28,
    cost: 6.94,
    company: "agentica",
    bold: true,
  },
  {
    name: "Opus 4.6 (120k) High",
    score: 79.03,
    cost: 3.81,
    company: "anthropic",
    bold: false,
  },
  {
    name: "Agentica GPT 5.2 (XHigh)",
    score: 70.27,
    cost: 5.03,
    company: "agentica",
    bold: true,
  },
  {
    name: "GPT 5.2 (XHigh)*",
    score: 59.81,
    cost: 2.05,
    company: "openai",
    bold: false,
  },
  {
    name: "Agentica Opus 4.5",
    score: 49.58,
    cost: 10.4,
    company: "agentica",
    bold: true,
  },
  {
    name: "Opus 4.5 (32k)",
    score: 28.15,
    cost: 1.37,
    company: "anthropic",
    bold: false,
  },
];

const companyNames = {
  agentica: "Agentica",
  openai: "OpenAI",
  anthropic: "Anthropic",
};

const legendItems = [
  { key: "agentica" as const, name: "Agentica" },
  { key: "openai" as const, name: "OpenAI" },
  { key: "anthropic" as const, name: "Anthropic" },
];

export function ScatterChart() {
  const { resolvedTheme } = useBlogTheme();
  const isDark = resolvedTheme === "dark";
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<{
    visible: boolean;
    x: number;
    y: number;
    item: DataPoint | null;
  }>({
    visible: false,
    x: 0,
    y: 0,
    item: null,
  });

  const colors = {
    agentica: isDark ? "#8b7bd4" : "#7668c7",
    openai: isDark ? "#999" : "#444",
    anthropic: "#9E4530",
  };

  // Chart dimensions
  const W = 900,
    H = 590;
  const pad = { t: 60, r: 30, b: 105, l: 60 };
  const fs = { axis: 17, axisTitle: 17, dot: 15, legend: 17, title: 22 };
  const dR = { b: 7, s: 5 };
  const pW = W - pad.l - pad.r;
  const pH = H - pad.t - pad.b;

  // Scale functions
  const minX = 0.1,
    maxX = 100;
  const xScale = (c: number) =>
    pad.l +
    ((Math.log10(c) - Math.log10(minX)) /
      (Math.log10(maxX) - Math.log10(minX))) *
      pW;
  const minY = 0,
    maxY = 100;
  const yScale = (s: number) => pad.t + pH - ((s - minY) / (maxY - minY)) * pH;

  const maxScore = Math.max(...data.map((d) => d.score));
  const xTicks = [0.1, 1, 10, 100];
  const yTicks = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

  // Legend positioning
  const iW = [130, 115, 135];
  const tW = iW.reduce((a, b) => a + b, 0) + 20;
  let legendStartX = pad.l + (pW - tW) / 2;

  const handleMouseEnter = (item: DataPoint, e: React.MouseEvent) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    let x = e.clientX - rect.left + 16;
    let y = e.clientY - rect.top - 10;
    if (x + 230 > rect.width) x = e.clientX - rect.left - 240;
    if (y + 120 > rect.height) y = y - 100;
    if (y < 10) y = 10;
    if (x < 10) x = 10;
    setTooltip({ visible: true, x, y, item });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!tooltip.visible || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    let x = e.clientX - rect.left + 16;
    let y = e.clientY - rect.top - 10;
    if (x + 230 > rect.width) x = e.clientX - rect.left - 240;
    if (y + 120 > rect.height) y = y - 100;
    if (y < 10) y = 10;
    if (x < 10) x = 10;
    setTooltip((prev) => ({ ...prev, x, y }));
  };

  const handleMouseLeave = () => {
    setTooltip({ visible: false, x: 0, y: 0, item: null });
  };

  const themeColors = {
    bg: isDark ? "#1a1a1a" : "#FFFDF0",
    text: isDark ? "#fff" : "#1a1a1a",
    axisLabel: isDark ? "#888" : "#555",
    axisTitle: isDark ? "#ccc" : "#1a1a1a",
    gridLine: isDark ? "#2a2a2a" : "#e0dfd9",
    axisLine: isDark ? "#444" : "#ccc",
    legendText: isDark ? "#999" : "#555",
    tooltipBg: isDark ? "#1e1e1e" : "#fff",
    tooltipBorder: isDark ? "#2a2a2a" : "#e0dfd9",
    tooltipText: isDark ? "#fff" : "#1a1a1a",
    tooltipLabel: "#888",
    tooltipVal: isDark ? "#e0e0e0" : "#1a1a1a",
    sotaColor: isDark ? "#E53935" : "#D32F2F",
  };

  return (
    <div style={{ width: "100%" }} className="scatter-chart__box">
      <div
        ref={containerRef}
        className="scatter-chart__container"
        style={{
          position: "relative",
          overflow: "visible",
          padding: "30px 40px",
        }}
      >
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="scatter-chart__svg"
          style={{
            width: "100%",
            height: "auto",
            display: "block",
            overflow: "visible",
          }}
        >
          {/* Title */}
          <text
            x={pad.l + pW / 2}
            y={pad.t - 35}
            textAnchor="middle"
            fill={themeColors.text}
            style={{
              fontSize: `${fs.title}px`,
              fontFamily: "'IBM Plex Mono', monospace",
              fontWeight: 600,
              letterSpacing: "0.05em",
            }}
          >
            ARC-AGI-2 Public Eval: Agentica vs. CoT
          </text>

          {/* Y-axis grid lines and labels */}
          {yTicks.map((s) => (
            <g key={`y-${s}`}>
              <line
                x1={pad.l}
                y1={yScale(s)}
                x2={W - pad.r}
                y2={yScale(s)}
                stroke={themeColors.gridLine}
                strokeWidth={1}
              />
              <text
                x={pad.l - 10}
                y={yScale(s) + 4}
                textAnchor="end"
                fill={themeColors.axisLabel}
                style={{
                  fontSize: `${fs.axis}px`,
                  fontFamily: "'IBM Plex Mono', monospace",
                }}
              >
                {s}%
              </text>
            </g>
          ))}

          {/* X-axis grid lines and labels */}
          {xTicks.map((v) => (
            <g key={`x-${v}`}>
              <line
                x1={xScale(v)}
                y1={pad.t}
                x2={xScale(v)}
                y2={H - pad.b}
                stroke={themeColors.gridLine}
                strokeWidth={1}
              />
              <text
                x={xScale(v)}
                y={H - pad.b + 20}
                textAnchor="middle"
                fill={themeColors.axisLabel}
                style={{
                  fontSize: `${fs.axis}px`,
                  fontFamily: "'IBM Plex Mono', monospace",
                }}
              >
                ${v}
              </text>
            </g>
          ))}

          {/* Axes */}
          <line
            x1={pad.l}
            y1={yScale(0)}
            x2={W - pad.r}
            y2={yScale(0)}
            stroke={themeColors.axisLine}
            strokeWidth={1}
          />
          <line
            x1={pad.l}
            y1={pad.t}
            x2={pad.l}
            y2={H - pad.b}
            stroke={themeColors.axisLine}
            strokeWidth={1}
          />

          {/* Axis titles */}
          <text
            x={pad.l + pW / 2}
            y={H - pad.b + 46}
            textAnchor="middle"
            fill={themeColors.axisTitle}
            style={{
              fontSize: `${fs.axisTitle}px`,
              fontFamily: "'IBM Plex Mono', monospace",
              fontWeight: 500,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            Cost Per Task ($)
          </text>
          <text
            x={-5}
            y={pad.t + pH / 2}
            textAnchor="middle"
            transform={`rotate(-90, -5, ${pad.t + pH / 2})`}
            fill={themeColors.axisTitle}
            style={{
              fontSize: `${fs.axisTitle}px`,
              fontFamily: "'IBM Plex Mono', monospace",
              fontWeight: 500,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            Score (%)
          </text>

          {/* Data points */}
          {data.map((item, i) => {
            const cx = xScale(item.cost);
            const cy = yScale(item.score);
            const col = colors[item.company];
            const r = item.bold ? dR.b : dR.s;
            const isSOTA = item.score === maxScore;
            const strokeCol = isSOTA ? themeColors.sotaColor : col;

            return (
              <g key={i}>
                {/* Outer ring for bold items */}
                {item.bold && (
                  <circle
                    cx={cx}
                    cy={cy}
                    r={r + 5}
                    fill="none"
                    stroke={isSOTA ? themeColors.sotaColor : col}
                    strokeWidth={1}
                    opacity={isSOTA ? 0.5 : 0.2}
                  />
                )}
                {/* SOTA label */}
                {isSOTA && (
                  <text
                    x={cx}
                    y={cy - 18}
                    textAnchor="middle"
                    fill={themeColors.sotaColor}
                    style={{
                      fontSize: "10px",
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontWeight: 700,
                      letterSpacing: "0.1em",
                      pointerEvents: "none",
                    }}
                  >
                    SOTA
                  </text>
                )}
                {/* Main dot */}
                <circle
                  cx={cx}
                  cy={cy}
                  r={r}
                  fill={col}
                  stroke={strokeCol}
                  strokeWidth={isSOTA ? 3 : 2}
                  fillOpacity={isDark ? 0.85 : 0.8}
                  style={{
                    cursor: "pointer",
                    transition: "filter 0.2s ease",
                  }}
                  onMouseEnter={(e) => handleMouseEnter(item, e)}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseLeave}
                />
                {/* Label */}
                <text
                  x={cx + r + 8}
                  y={cy + 4}
                  textAnchor="start"
                  fill={col}
                  opacity={isDark ? 0.9 : 0.85}
                  style={{
                    fontSize: `${fs.dot}px`,
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontWeight: item.bold ? 500 : isDark ? 300 : 400,
                    pointerEvents: "none",
                  }}
                >
                  {item.name}
                  {item.subLabel && (
                    <tspan x={cx + r + 8} dy="1.2em" textAnchor="start">
                      {item.subLabel}
                    </tspan>
                  )}
                </text>
              </g>
            );
          })}

          {/* Legend */}
          {legendItems.map((li, i) => {
            const col = colors[li.key];
            const x =
              legendStartX + iW.slice(0, i).reduce((a, b) => a + b + 10, 0);
            return (
              <g key={li.key}>
                <circle
                  cx={x + 5}
                  cy={H - 20}
                  r={5}
                  fill={col}
                  opacity={isDark ? 0.85 : 0.8}
                />
                <text
                  x={x + 14}
                  y={H - 12}
                  fill={themeColors.legendText}
                  style={{
                    fontSize: `${fs.legend}px`,
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontWeight: isDark ? 300 : 400,
                  }}
                >
                  {li.name}
                </text>
              </g>
            );
          })}
        </svg>

        {/* Tooltip */}
        {tooltip.visible && tooltip.item && (
          <div
            style={{
              position: "absolute",
              left: tooltip.x,
              top: tooltip.y,
              pointerEvents: "none",
              background: themeColors.tooltipBg,
              border: `1px solid ${themeColors.tooltipBorder}`,
              borderRadius: "10px",
              padding: "14px 18px",
              zIndex: 100,
              minWidth: "220px",
              boxShadow: isDark
                ? "0 4px 20px rgba(0,0,0,.4)"
                : "0 4px 20px rgba(0,0,0,.08)",
            }}
          >
            <div
              style={{
                width: "100%",
                height: "3px",
                borderRadius: "2px",
                marginBottom: "10px",
                background: colors[tooltip.item.company],
              }}
            />
            <div
              style={{
                fontSize: "0.78rem",
                fontWeight: 600,
                color: themeColors.tooltipText,
                marginBottom: "8px",
                lineHeight: 1.3,
                fontFamily: "'IBM Plex Mono', monospace",
              }}
            >
              {tooltip.item.name}
              {tooltip.item.subLabel ? ` (${tooltip.item.subLabel})` : ""}
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "4px",
                fontSize: "0.72rem",
                fontFamily: "'IBM Plex Mono', monospace",
              }}
            >
              <span
                style={{ color: themeColors.tooltipLabel, fontWeight: 300 }}
              >
                Eval Score
              </span>
              <span style={{ color: themeColors.tooltipVal, fontWeight: 400 }}>
                {tooltip.item.score}%
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "4px",
                fontSize: "0.72rem",
                fontFamily: "'IBM Plex Mono', monospace",
              }}
            >
              <span
                style={{ color: themeColors.tooltipLabel, fontWeight: 300 }}
              >
                Cost/Task
              </span>
              <span style={{ color: themeColors.tooltipVal, fontWeight: 400 }}>
                ${tooltip.item.cost.toFixed(2)}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "4px",
                fontSize: "0.72rem",
                fontFamily: "'IBM Plex Mono', monospace",
              }}
            >
              <span
                style={{ color: themeColors.tooltipLabel, fontWeight: 300 }}
              >
                Author
              </span>
              <span style={{ color: themeColors.tooltipVal, fontWeight: 400 }}>
                {companyNames[tooltip.item.company]}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
